import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { TenantsList, type CreateTenantResult } from '../../features/tenants/tenants-list';
import { plansKey, tenantsListKey } from '../../features/tenants/use-tenant';
import type { CreateTenantRequest, TenantStatus } from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';
import { useStepUp } from '../../lib/session/step-up';

export const Route = createFileRoute('/tenants/')({
  component: TenantsPage,
});

/**
 * `/tenants` (MASTER_PLAN M12.7, Docs/API.md §2.13).
 *
 * The route is the only thing here that knows about the network: it fetches, it mutates,
 * and it hands `<TenantsList>` plain data and callbacks. Swapping the mock client for the
 * real one in M12.1 changes nothing below this file (rules §17.1).
 */
function TenantsPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { guard } = useStepUp();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | 'all'>('all');
  const [createError, setCreateError] = useState<string | undefined>(undefined);
  const [createResult, setCreateResult] = useState<CreateTenantResult | undefined>(undefined);

  /*
   * Cursor paging, not an offset: the cursor encodes the sort and is opaque, so a page
   * boundary stays put while clinics are being created (API.md §1.7).
   */
  const tenants = useInfiniteQuery({
    queryKey: tenantsListKey(search, status),
    queryFn: ({ pageParam }) =>
      client.listTenants({ q: search, status, limit: 25, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
  });
  const plans = useQuery({ queryKey: plansKey(), queryFn: () => client.listPlans() });

  /*
   * The invitation is reported from the **answer**, not from the request: the endpoint
   * accepts `adminEmail` and, as built, still answers `invitation: null` (API.md §2.13).
   * The banner above the list says which of those happened, because "invited" when nobody
   * was is the one mistake here that costs a clinic a week.
   */
  const create = useMutation({
    /*
     * `POST /platform/tenants` is a `@StepUp()` route, so it goes through `guard` like
     * every other step-up write on this console (`SUPER_ADMIN_UI.md` item 44). Without
     * it the un-wrapped path raised a dialog with nothing to retry, and an operator who
     * verified then had to type the whole clinic in again.
     */
    mutationFn: (input: CreateTenantRequest) =>
      guard(() => client.createTenant(input), 'create this clinic'),
    onSuccess: async ({ tenant, invitation }, input) => {
      setCreateError(undefined);
      setCreateResult({
        tenantName: tenant.name,
        requestedAdminEmail: input.adminEmail ?? null,
        invitation,
      });
      toast({
        tone: 'success',
        message:
          invitation === null
            ? `${tenant.name} created. Its subdomains are live.`
            : `${tenant.name} created. An invitation was sent to ${invitation.email}.`,
      });
      await queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
    onError: (error: Error) => {
      setCreateError(error.message);
    },
  });

  if (tenants.isPending || plans.isPending) {
    return (
      <>
        <PageHeader title="Tenants" />
        <Card className="p-6">
          <Spinner label="Loading tenants" />
        </Card>
      </>
    );
  }

  if (tenants.isError || plans.isError) {
    return (
      <>
        <PageHeader title="Tenants" />
        <Alert tone="error">The tenant list could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <TenantsList
      tenants={tenants.data.pages.flatMap((page) => page.data)}
      hasMore={tenants.hasNextPage}
      loadingMore={tenants.isFetchingNextPage}
      onLoadMore={() => {
        void tenants.fetchNextPage();
      }}
      plans={plans.data}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      createError={createError}
      createResult={createResult}
      onDismissCreateResult={() => {
        setCreateResult(undefined);
      }}
      onCreate={(input) => {
        setCreateResult(undefined);
        create.mutate(input);
      }}
      renderLink={(tenant, children) => (
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: tenant.id }}
          className="font-medium text-primary-accessible hover:underline"
        >
          {children}
        </Link>
      )}
    />
  );
}
